import React, { useState, useEffect } from "react";
import {
  Plus,
  Download,
  Trash2,
  FileText,
  Save,
  Eye,
  CreditCard,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import { toast } from "react-hot-toast";
import api from "@/services/api";
import InvoicePreview from "./components/InvoicePreview";
import {
  getSubsidiaryLogo,
  getSubsidiaryName,
} from "@/utils/subsidiaryHelpers";

const InvoiceGeneration = () => {
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Calculate default due date (30 days from today)
  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split("T")[0];
  };

  const [invoiceData, setInvoiceData] = useState({
    subsidiary: "puffin_group",
    invoice_number: "",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: getDefaultDueDate(),
    bill_to_name: "",
    bill_to_address: "",
    bill_to_email: "",
    bill_to_phone: "",
    items: [
      {
        description: "",
        quantity: 1,
        unit_price: 0,
      },
    ],
    additional_charges: [], // VAT, shipping, etc.
    deductions: [], // Discounts, etc.
    notes: "",
    account_details: "",
  });

  useEffect(() => {
    loadSubsidiaries();
    loadInvoices();
    generateInvoiceNumber();
  }, []);

  // Helper function to convert entity_id to subsidiary code
  const getSubsidiaryCodeFromEntityId = (entityId) => {
    const entityMap = {
      1: "puffin_group",
      2: "buildwise",
      3: "laser_shield",
      4: "maz_logistics",
      5: "puffin_energy",
      6: "swingtide",
      7: "puffin_agro",
    };
    return entityMap[entityId] || "puffin_group";
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get("/invoices");
      setInvoices(response.data?.data?.invoices || []);
    } catch (error) {
      console.error("Error loading invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubsidiaries = async () => {
    try {
      const response = await api.get("/finance/subsidiaries");
      setSubsidiaries(response.data?.data?.subsidiaries || []);
    } catch (error) {
      console.error("Error loading subsidiaries:", error);
    }
  };

  const generateInvoiceNumber = () => {
    const prefix = "PG";
    const timestamp = Date.now().toString().slice(-6);
    setInvoiceData((prev) => ({
      ...prev,
      invoice_number: `${prefix}-${timestamp}`,
    }));
  };

  const handleAddItem = () => {
    setInvoiceData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: "",
          quantity: 1,
          unit_price: 0,
        },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    if (invoiceData.items.length === 1) {
      toast.error("Invoice must have at least one item");
      return;
    }
    setInvoiceData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setInvoiceData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const calculateSubtotal = () => {
    return invoiceData.items.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0,
    );
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    
    // Add additional charges (can be percentage or fixed)
    const charges = (invoiceData.additional_charges || []).reduce((sum, charge) => {
      const amount = parseFloat(charge.amount) || 0;
      if (charge.type === 'percentage') {
        return sum + (subtotal * amount / 100);
      }
      return sum + amount;
    }, 0);
    
    // Subtract deductions (can be percentage or fixed)
    const deductions = (invoiceData.deductions || []).reduce((sum, deduction) => {
      const amount = parseFloat(deduction.amount) || 0;
      if (deduction.type === 'percentage') {
        return sum + (subtotal * amount / 100);
      }
      return sum + amount;
    }, 0);
    
    return subtotal + charges - deductions;
  };

  const addCharge = () => {
    setInvoiceData({
      ...invoiceData,
      additional_charges: [...(invoiceData.additional_charges || []), { name: '', amount: 0, type: 'fixed' }]
    });
  };

  const addDeduction = () => {
    setInvoiceData({
      ...invoiceData,
      deductions: [...(invoiceData.deductions || []), { name: '', amount: 0, type: 'fixed' }]
    });
  };

  const removeCharge = (index) => {
    const charges = [...invoiceData.additional_charges];
    charges.splice(index, 1);
    setInvoiceData({ ...invoiceData, additional_charges: charges });
  };

  const removeDeduction = (index) => {
    const deductions = [...invoiceData.deductions];
    deductions.splice(index, 1);
    setInvoiceData({ ...invoiceData, deductions: deductions });
  };

  const updateCharge = (index, field, value) => {
    const charges = [...invoiceData.additional_charges];
    charges[index][field] = value;
    setInvoiceData({ ...invoiceData, additional_charges: charges });
  };

  const updateDeduction = (index, field, value) => {
    const deductions = [...invoiceData.deductions];
    deductions[index][field] = value;
    setInvoiceData({ ...invoiceData, deductions: deductions });
  };

  const handleSaveInvoice = async () => {
    try {
      setLoading(true);

      // Validation
      if (!invoiceData.bill_to_name) {
        toast.error("Please enter customer name");
        return;
      }

      if (!invoiceData.due_date) {
        toast.error("Please enter due date");
        return;
      }

      // Validate due_date is a valid date
      const dueDate = new Date(invoiceData.due_date);
      if (isNaN(dueDate.getTime())) {
        toast.error("Please enter a valid due date");
        return;
      }

      if (invoiceData.items.some((item) => !item.description)) {
        toast.error("Please fill in all item descriptions");
        return;
      }

      const payload = {
        ...invoiceData,
        subtotal: calculateSubtotal(),
        total: calculateTotal(),
        status: editingInvoice ? editingInvoice.status : "draft",
      };

      const response = editingInvoice
        ? await api.put(`/invoices/${editingInvoice.id}`, payload)
        : await api.post("/invoices", payload);

      if (response.data.success) {
        toast.success(editingInvoice ? "Invoice updated successfully" : "Invoice saved successfully");
        setShowForm(false);
        setEditingInvoice(null);
        resetForm();
        loadInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId = null) => {
    try {
      setLoading(true);

      const id = invoiceId || editingInvoice?.id;

      if (!id) {
        // Creating new invoice and marking as paid
        // Validation
        if (!invoiceData.bill_to_name) {
          toast.error("Please enter customer name");
          return;
        }

        if (!invoiceData.due_date) {
          toast.error("Please enter due date");
          return;
        }

        // Validate due_date is a valid date
        const dueDate = new Date(invoiceData.due_date);
        if (isNaN(dueDate.getTime())) {
          toast.error("Please enter a valid due date");
          return;
        }

        if (invoiceData.items.some((item) => !item.description)) {
          toast.error("Please fill in all item descriptions");
          return;
        }

        const payload = {
          ...invoiceData,
          subtotal: calculateSubtotal(),
          total: calculateTotal(),
          status: "paid",
          payment_date: new Date().toISOString(),
        };

        const response = await api.post("/invoices", payload);

        if (response.data.success) {
          toast.success("Invoice marked as paid");
          setShowForm(false);
          resetForm();
          loadInvoices();
        }
      } else {
        // Marking existing invoice as paid
        const response = await api.post(`/invoices/${id}/mark-paid`, {
          payment_date: new Date().toISOString(),
        });

        if (response.data.success) {
          toast.success("Invoice marked as paid");
          loadInvoices();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to process payment");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!confirm("Are you sure you want to delete this invoice?")) return;

    try {
      setLoading(true);
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted successfully");
      loadInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = async (invoice) => {
    try {
      setLoading(true);
      const response = await api.get(`/invoices/${invoice.id}`);
      const fullInvoice = response.data?.data?.invoice;

      setEditingInvoice(fullInvoice);
      setInvoiceData({
        subsidiary: getSubsidiaryCodeFromEntityId(fullInvoice.entity_id),
        invoice_number: fullInvoice.invoice_number,
        invoice_date: fullInvoice.invoice_date,
        due_date: fullInvoice.due_date,
        bill_to_name: fullInvoice.Customer?.company_name || "",
        bill_to_address: fullInvoice.Customer?.billing_address || "",
        bill_to_email: fullInvoice.Customer?.email || "",
        bill_to_phone: fullInvoice.Customer?.phone || "",
        items:
          fullInvoice.items?.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
          })) || [],
        notes: fullInvoice.notes || "",
        account_details: fullInvoice.terms_conditions || "",
      });
      setShowForm(true);
    } catch (error) {
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleNewInvoice = () => {
    setEditingInvoice(null);
    resetForm();
    setShowForm(true);
  };

  const resetForm = () => {
    setInvoiceData({
      subsidiary: "puffin_group",
      invoice_number: "",
      invoice_date: new Date().toISOString().split("T")[0],
      due_date: getDefaultDueDate(),
      bill_to_name: "",
      bill_to_address: "",
      bill_to_email: "",
      bill_to_phone: "",
      items: [
        {
          description: "",
          quantity: 1,
          unit_price: 0,
        },
      ],
      notes: "",
      account_details: "",
    });
    generateInvoiceNumber();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: "bg-gray-100 text-gray-800", icon: Clock },
      sent: { color: "bg-blue-100 text-blue-800", icon: FileText },
      paid: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      cancelled: { color: "bg-red-100 text-red-800", icon: XCircle },
      overdue: { color: "bg-orange-100 text-orange-800", icon: Clock },
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">
            Create and manage customer invoices
          </p>
        </div>
        <button
          onClick={handleNewInvoice}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Invoice
        </button>
      </div>

      {/* Invoices List */}
      {!showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              All Invoices ({invoices.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-500">No invoices yet</p>
                      <button
                        onClick={handleNewInvoice}
                        className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Create your first invoice
                      </button>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => {
                    const statusBadge = getStatusBadge(invoice.status);
                    const StatusIcon = statusBadge.icon;
                    return (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">
                            {invoice.invoice_number}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {invoice.Customer?.company_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {invoice.Customer?.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.invoice_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(invoice.total)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setInvoiceData({
                                  ...invoiceData,
                                  subsidiary: getSubsidiaryCodeFromEntityId(
                                    invoice.entity_id,
                                  ),
                                  invoice_number: invoice.invoice_number,
                                  invoice_date: invoice.invoice_date,
                                  due_date: invoice.due_date,
                                  bill_to_name: invoice.Customer?.company_name,
                                  bill_to_email: invoice.Customer?.email,
                                  bill_to_phone: invoice.Customer?.phone,
                                  bill_to_address:
                                    invoice.Customer?.billing_address,
                                  items: invoice.items || [],
                                  notes: invoice.notes,
                                  account_details: invoice.terms_conditions,
                                  subtotal: invoice.subtotal,
                                  total: invoice.total,
                                });
                                setShowPreview(true);
                              }}
                              className="text-gray-600 hover:text-gray-900"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditInvoice(invoice)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {invoice.status !== "paid" && (
                              <button
                                onClick={() => handleMarkAsPaid(invoice.id)}
                                className="text-green-600 hover:text-green-900"
                                title="Mark as Paid"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Form */}
      {showForm && (
        <div className="space-y-6">
          {/* Form Header */}
          <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingInvoice ? "Edit Invoice" : "New Invoice"}
            </h2>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={handleSaveInvoice}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                Save Draft
              </button>
              <button
                onClick={() => handleMarkAsPaid()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <CreditCard className="w-4 h-4" />
                Save & Mark Paid
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {/* Subsidiary Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Subsidiary
              </label>
              <div className="flex items-center gap-4">
                <select
                  value={invoiceData.subsidiary}
                  onChange={(e) =>
                    setInvoiceData({
                      ...invoiceData,
                      subsidiary: e.target.value,
                    })
                  }
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="puffin_group">Puffin Group Ltd</option>
                  <option value="buildwise">Buildwise Construction Ltd</option>
                  <option value="laser_shield">Laser Shield Ltd.</option>
                  <option value="maz_logistics">Maz Logistics Ltd.</option>
                  <option value="puffin_energy">Puffin Energy Ltd.</option>
                  <option value="swingtide">Swingtide Consulting Ltd</option>
                  <option value="puffin_agro">Puffin Agro Allied</option>
                </select>
                {/* Logo Preview */}
                <div className="flex-shrink-0 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="w-20 h-20 flex items-center justify-center mb-2">
                    <img
                      src={getSubsidiaryLogo(invoiceData.subsidiary)}
                      alt="Subsidiary Logo"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.src = "/puffin_icon.png";
                      }}
                    />
                  </div>
                  <p className="text-xs text-center text-gray-600 font-medium leading-tight">
                    {getSubsidiaryName(invoiceData.subsidiary)}
                  </p>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Number
                </label>
                <input
                  type="text"
                  value={invoiceData.invoice_number}
                  onChange={(e) =>
                    setInvoiceData({
                      ...invoiceData,
                      invoice_number: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Date
                </label>
                <input
                  type="date"
                  value={invoiceData.invoice_date}
                  onChange={(e) =>
                    setInvoiceData({
                      ...invoiceData,
                      invoice_date: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={invoiceData.due_date}
                  onChange={(e) =>
                    setInvoiceData({ ...invoiceData, due_date: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Bill To Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Bill To
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Customer Name *
                  </label>
                  <input
                    type="text"
                    value={invoiceData.bill_to_name}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        bill_to_name: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={invoiceData.bill_to_email}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        bill_to_email: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={invoiceData.bill_to_phone}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        bill_to_phone: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    value={invoiceData.bill_to_address}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        bill_to_address: e.target.value,
                      })
                    }
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Items</h3>
                <button
                  onClick={handleAddItem}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>

              <div className="space-y-4">
                {invoiceData.items.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-12 gap-4 items-start p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="col-span-5">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Item description"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "quantity",
                            parseInt(e.target.value) || 1,
                          )
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unit Price (₦)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "unit_price",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount
                      </label>
                      <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 font-medium">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </div>
                    </div>
                    <div className="col-span-1 flex items-end">
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Charges & Deductions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Additional Charges */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Additional Charges</h3>
                  <button
                    type="button"
                    onClick={addCharge}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" /> Add
                  </button>
                </div>
                {(invoiceData.additional_charges || []).map((charge, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Name (e.g., VAT, Shipping)"
                      value={charge.name}
                      onChange={(e) => updateCharge(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <select
                      value={charge.type || 'fixed'}
                      onChange={(e) => updateCharge(index, 'type', e.target.value)}
                      className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="fixed">₦</option>
                      <option value="percentage">%</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Amount"
                      value={charge.amount}
                      onChange={(e) => updateCharge(index, 'amount', e.target.value)}
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeCharge(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Deductions */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Deductions</h3>
                  <button
                    type="button"
                    onClick={addDeduction}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" /> Add
                  </button>
                </div>
                {(invoiceData.deductions || []).map((deduction, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Name (e.g., Discount)"
                      value={deduction.name}
                      onChange={(e) => updateDeduction(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <select
                      value={deduction.type || 'fixed'}
                      onChange={(e) => updateDeduction(index, 'type', e.target.value)}
                      className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="fixed">₦</option>
                      <option value="percentage">%</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Amount"
                      value={deduction.amount}
                      onChange={(e) => updateDeduction(index, 'amount', e.target.value)}
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeDeduction(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-full md:w-1/3 space-y-2">
                <div className="flex justify-between items-center py-2 border-t border-gray-200">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(calculateSubtotal())}
                  </span>
                </div>
                {(invoiceData.additional_charges || []).map((charge, index) => {
                  const subtotal = calculateSubtotal();
                  const amount = charge.type === 'percentage' 
                    ? (subtotal * (parseFloat(charge.amount) || 0) / 100)
                    : (parseFloat(charge.amount) || 0);
                  return (
                    <div key={index} className="flex justify-between items-center py-1 text-sm">
                      <span className="text-green-600">
                        + {charge.name || 'Charge'} {charge.type === 'percentage' ? `(${charge.amount}%)` : ''}:
                      </span>
                      <span className="text-green-600">{formatCurrency(amount)}</span>
                    </div>
                  );
                })}
                {(invoiceData.deductions || []).map((deduction, index) => {
                  const subtotal = calculateSubtotal();
                  const amount = deduction.type === 'percentage' 
                    ? (subtotal * (parseFloat(deduction.amount) || 0) / 100)
                    : (parseFloat(deduction.amount) || 0);
                  return (
                    <div key={index} className="flex justify-between items-center py-1 text-sm">
                      <span className="text-red-600">
                        - {deduction.name || 'Deduction'} {deduction.type === 'percentage' ? `(${deduction.amount}%)` : ''}:
                      </span>
                      <span className="text-red-600">{formatCurrency(amount)}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between items-center py-2 border-t-2 border-gray-300">
                  <span className="text-lg font-bold text-gray-900">
                    Balance Due:
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes / Terms
                </label>
                <textarea
                  value={invoiceData.notes}
                  onChange={(e) =>
                    setInvoiceData({ ...invoiceData, notes: e.target.value })
                  }
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Payment terms, thank you message, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Details
                </label>
                <textarea
                  value={invoiceData.account_details}
                  onChange={(e) =>
                    setInvoiceData({
                      ...invoiceData,
                      account_details: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Bank account details for payment"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <InvoicePreview
          invoiceData={{
            ...invoiceData,
            subtotal: calculateSubtotal(),
            total: calculateTotal(),
          }}
          onClose={() => setShowPreview(false)}
          onDownload={() => {
            // Use browser print to save as PDF
            window.print();
          }}
        />
      )}
    </div>
  );
};

export default InvoiceGeneration;
